/**
 * IndexedDB-backed implementation of `KeyStore` for the PWA.
 *
 * Stores opaque byte arrays under string keys in a single object store.
 * Pair with `SealedKeyStore` from @chat/crypto to encrypt values at rest.
 */
import type { KeyStore } from '@chat/crypto';

function toBytes(value: unknown, key: string): Uint8Array {
  if (value instanceof Uint8Array) return new Uint8Array(value);
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (ArrayBuffer.isView(value)) return new Uint8Array(value.buffer.slice(0));
  if (Array.isArray(value) && value.every((n) => typeof n === 'number')) {
    return new Uint8Array(value);
  }
  throw new Error(`unexpected stored type for ${key}`);
}

const DEFAULT_DB = 'chat-keystore';
const DEFAULT_STORE = 'entries';

function openDb(dbName: string, storeName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName);
      }
    };
    req.onsuccess = () => {
      resolve(req.result);
    };
    req.onerror = () => {
      reject(req.error ?? new Error('indexeddb open failed'));
    };
  });
}

function run<T>(
  db: IDBDatabase,
  storeName: string,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    const req = fn(store);
    req.onsuccess = () => {
      resolve(req.result);
    };
    req.onerror = () => {
      reject(req.error ?? new Error('indexeddb op failed'));
    };
  });
}

export class IndexedDbKeyStore implements KeyStore {
  readonly #dbName: string;
  readonly #storeName: string;
  #db: IDBDatabase | undefined;

  constructor(options: { dbName?: string; storeName?: string } = {}) {
    this.#dbName = options.dbName ?? DEFAULT_DB;
    this.#storeName = options.storeName ?? DEFAULT_STORE;
  }

  async #ensureDb(): Promise<IDBDatabase> {
    this.#db ??= await openDb(this.#dbName, this.#storeName);
    return this.#db;
  }

  async get(key: string): Promise<Uint8Array | undefined> {
    const db = await this.#ensureDb();
    const result = await run<unknown>(db, this.#storeName, 'readonly', (s) => s.get(key));
    if (result === undefined) return undefined;
    return toBytes(result, key);
  }

  async put(key: string, value: Uint8Array): Promise<void> {
    const db = await this.#ensureDb();
    await run<IDBValidKey>(db, this.#storeName, 'readwrite', (s) =>
      s.put(new Uint8Array(value), key),
    );
  }

  async delete(key: string): Promise<void> {
    const db = await this.#ensureDb();
    await run<undefined>(db, this.#storeName, 'readwrite', (s) => s.delete(key));
  }

  async list(prefix = ''): Promise<readonly string[]> {
    const db = await this.#ensureDb();
    return new Promise((resolve, reject) => {
      const out: string[] = [];
      const tx = db.transaction(this.#storeName, 'readonly');
      const req = tx.objectStore(this.#storeName).openKeyCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          const rawKey = cursor.key;
          if (typeof rawKey === 'string' && rawKey.startsWith(prefix)) out.push(rawKey);
          cursor.continue();
        } else {
          resolve(out);
        }
      };
      req.onerror = () => {
        reject(req.error ?? new Error('indexeddb cursor failed'));
      };
    });
  }
}
